import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from 'next-themes';
import { Sun, Moon, Menu, X, LayoutDashboard } from 'lucide-react';
import LocaleLink from './LocaleLink';

const LOGO_URL = 'https://quidory.com/media/quidory-logo.png';

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: t('nav.products'), path: '/' },
    { label: t('nav.features'), path: '/features' },
    { label: t('nav.pricing'), path: '/pricing' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'glass border-b border-border shadow-sm'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <LocaleLink
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2.5 shrink-0 group"
          >
            <img
              src={LOGO_URL}
              alt="Quidory"
              className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-110"
            />
            <span className="font-semibold text-base tracking-tight text-foreground">
              {language === 'ar' ? 'كويدوري' : 'Quidory'}
            </span>
          </LocaleLink>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <LocaleLink
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </LocaleLink>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1.5">
            {/* Language Toggle */}
            <button
              data-testid="language-toggle"
              onClick={toggleLanguage}
              aria-label="Toggle language"
              className="px-3 py-1.5 text-xs font-semibold rounded-md border border-border/70 text-muted-foreground hover:text-foreground hover:border-border transition-all duration-200"
            >
              {language === 'ar' ? 'EN' : 'AR'}
            </button>

            {/* Theme Toggle */}
            {mounted && (
              <button
                data-testid="theme-toggle"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                aria-label="Toggle theme"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
              >
                {theme === 'dark'
                  ? <Sun className="h-4 w-4" />
                  : <Moon className="h-4 w-4" />
                }
              </button>
            )}

            {/* Desktop Auth */}
            <div className="hidden md:flex items-center gap-1.5 ms-1">
              <LocaleLink
                to="/login"
                data-testid="signin-link"
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {t('nav.login')}
              </LocaleLink>
              <LocaleLink
                to="/signup"
                data-testid="signup-btn"
                className="px-4 py-2 text-sm font-medium rounded-md bg-foreground text-background hover:bg-foreground/90 transition-colors duration-200"
              >
                {t('nav.signup')}
              </LocaleLink>
              <LocaleLink
                to="/dashboard"
                data-testid="dashboard-btn"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
                title={t('nav.dashboard')}
              >
                <LayoutDashboard className="h-4 w-4" />
              </LocaleLink>
            </div>

            {/* Mobile Hamburger */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground transition-colors duration-200"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden glass border-t border-border">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <LocaleLink
                key={link.path}
                to={link.path}
                className={`block px-3 py-2.5 text-sm rounded-md transition-colors ${
                  isActive(link.path)
                    ? 'text-foreground font-medium bg-muted'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {link.label}
              </LocaleLink>
            ))}
            <div className="pt-3 border-t border-border space-y-2">
              <LocaleLink
                to="/login"
                className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {t('nav.login')}
              </LocaleLink>
              <LocaleLink
                to="/signup"
                className="block px-3 py-2.5 text-sm font-medium text-center rounded-md bg-foreground text-background"
              >
                {t('nav.signup')}
              </LocaleLink>
              <LocaleLink
                to="/dashboard"
                className="block px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {t('nav.dashboard')}
              </LocaleLink>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
